import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, Button, Divider } from '@material-ui/core';

// CONSTANTS
import { siteUrl } from '@/constants/common';

// UTILITY
import { routeEnhance } from '@/utility/routes';
import { analyticsInternalLinks } from '@/utility/analytics';

// COMPONENTS
import ReactHtml from '@/components/ReactHtml';

// STYLES
import { CardStyled } from './styles';

const SubscriptionPlanCard = ({
  data,
  partner,
  isActive,
  linkToSubscribe,
  trialDays,
  location,
  hideExternal,
}) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const partnerSettings = partner.settings || {};
  const isMybundleCheckout = partnerSettings.mybundle_checkout;

  function handleButtonClick() {
    let url = linkToSubscribe;

    if (isMybundleCheckout) {
      url = siteUrl + url;
    }

    const routeEnhanced = routeEnhance(url, { state: { location } });

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    if (partnerSettings.mybundle_checkout) {
      window.open(
        `${routeEnhanced.pathname}?${routeEnhanced.search}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      navigate(routeEnhanced);
    }
  }

  return (
    <CardStyled className={clsx('package-card', isActive && 'package-card--active')}>
      <Typography className="title" variant="h3">
        {data.name}
      </Typography>
      <div className="package-card__price">
        <span className="price">${data.price}</span>
        <span className="period">/{data.interval || t('common.month')}</span>
      </div>
      <div className="package-card__divider">
        <Divider />
      </div>
      <div className="package-card__about">
        <ReactHtml className="markdown">{data.description}</ReactHtml>
        {!!trialDays && (
          <Typography variant="body2">
            {t('appDetails.subscription.trial_period', { days: trialDays })}
          </Typography>
        )}
      </div>
      {!hideExternal && (
        <Button onClick={handleButtonClick} color="primary" size="medium" fullWidth>
          {isActive ? t('buttons.manage') : t('buttons.subscribe')}
        </Button>
      )}
    </CardStyled>
  );
};

SubscriptionPlanCard.defaultProps = {
  isActive: false,
  location: '',
  hideExternal: false,
};

SubscriptionPlanCard.propTypes = {
  data: PropTypes.object.isRequired,
  linkToSubscribe: PropTypes.string.isRequired,
  partner: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  trialDays: PropTypes.number,
  location: PropTypes.string,
  hideExternal: PropTypes.bool,
};

export default React.memo(SubscriptionPlanCard);
